'use client';

import React, { useEffect, useRef } from 'react';

import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const trustBadges = [
'Trusted Since 2013',
'HIPAA-Compliant',
'Fully Licensed',
'All Major Insurance Accepted',
'Private Pay Welcome'];



export default function HeroSection() {
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = badgeRef?.current;
    if (!el) return;
    el.style.opacity = '1';
  }, []);

  return (
    <section
      className="relative w-full min-h-screen bg-primary overflow-hidden"
      aria-label="Hero">
      
      {/* Background atmosphere */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="blob-navy absolute top-0 left-0 w-2/3 h-2/3 opacity-60" />
        <div className="blob-gold absolute bottom-0 right-0 w-1/2 h-1/2 opacity-20" />
      </div>
      {/* Fixed vertical grid lines decoration */}
      <div className="absolute inset-0 pointer-events-none hidden lg:flex justify-between px-10 max-w-screen-xl mx-auto left-0 right-0" aria-hidden="true">
        <div className="w-px h-full bg-white/5" />
        <div className="w-px h-full bg-white/5" />
        <div className="w-px h-full bg-white/5" />
        <div className="w-px h-full bg-white/5" />
      </div>
      <div className="relative z-10 max-w-screen-xl mx-auto px-6 md:px-10 pt-28 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start min-h-screen">

        {/* Left Content — lg:col-span-5 */}
        <div className="lg:col-span-5 flex flex-col pb-8 lg:pb-16">

          {/* Eyebrow */}
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 py-1.5 px-4 border border-white/20 rounded-full text-xs uppercase tracking-widest text-white/70 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" aria-hidden="true" />
              Northern New Jersey NEMT
            </span>
          </div>

          {/* H1 */}
          <h1 className="text-hero-xl font-display font-light text-white mb-6 leading-[0.95]">
            Safe,{' '}
            <span className="italic text-white/60">Comfortable</span>
            <br />
            Medical{' '}
            <span className="italic text-accent">Transport</span>
            <br />
            in Northern NJ
          </h1>

          {/* Subheadline */}
          <p className="text-base text-white/65 leading-relaxed max-w-sm mb-10 border-l-2 border-accent/50 pl-4">
            Non-emergency stretcher and wheelchair transport for dialysis, hospital discharge, and nursing home patients.{' '}
            <strong className="text-white/90 font-medium">We treat your family like our own.</strong>
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <a
              href="tel:+15513269573"
              className="btn-primary"
              aria-label="Call to book a quote at (551) 326-9573">
              
              <Icon name="PhoneIcon" size={16} />
              Book a Call for a Quote
            </a>
            <a
              href="#request-transport"
              className="btn-secondary"
              aria-label="Scroll to request transport form">
              
              Request Transport
              <Icon name="ArrowDownIcon" size={14} />
            </a>
          </div>

          {/* Trust strip — leads with "Trusted Since 2013" */}
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {trustBadges?.map((badge) =>
            <span
              key={badge}
              className="flex items-center gap-1.5 text-xs text-white/50 font-medium">
                <Icon name="CheckCircleIcon" size={13} className="text-accent/80" variant="solid" />
                {badge}
              </span>
            )}
          </div>
        </div>

        {/* Right Image — lg:col-span-7 */}
        <div className="lg:col-span-7 relative order-first lg:order-last self-start">
          <div className="w-full aspect-[16/9] sm:aspect-[16/9] lg:aspect-auto lg:h-[80vh] relative flex items-center justify-center">
          {/* Arch-shaped image container */}
          <div className="absolute inset-0 rounded-arch overflow-hidden shadow-navy group bg-primary">
            {/* PLACEHOLDER — replace with real photo of Kapra vehicle or patient transport */}
            <AppImage
              src="/assets/images/462C0D44-493D-4715-82AB-42BDB1C80A16-1781284781888.png"
              alt="Kapra Medical Transportation ambulance vehicle, professional emergency medical transport, clean white ambulance exterior"
              fill
              className="object-cover transition-transform duration-[2s] group-hover:scale-105"
              style={{ objectPosition: 'center 65%' }}
              priority
              sizes="(max-width: 1024px) 100vw, 60vw" />
            
            {/* Scrim: dark at bottom where text would overlap */}
            <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/10 to-transparent" aria-hidden="true" />

            {/* Floating trust card */}
            <div className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-md border border-white/40 p-4 rounded-xl max-w-[220px] hidden md:block shadow-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="ShieldCheckIcon" size={16} className="text-accent" />
                <span className="text-xs font-semibold text-primary">HIPAA Compliant</span>
              </div>
              <p className="text-xs leading-relaxed text-primary/70">
                Your medical information is always protected with us.
              </p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>);

}